import { useEffect, useState } from "react";
import axios from "axios";
import { useAdmin, adminAxios } from "./AdminContext";
import { SERVICES } from "@/data/site";
import { BookMarked, Plus, Pencil, Trash2, Save, X, Upload, CheckCircle2, AlertCircle, Image as ImageIcon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SERVICE_OPTIONS = SERVICES.map(s => s.title);

const empty = { name: "", logo_url: "", services: [], display_order: 0, active: true };

export default function ReferencesAdmin() {
  const { token } = useAdmin();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null | empty | item
  const [status, setStatus] = useState({ state: "idle", msg: "" });
  const [uploading, setUploading] = useState(false);

  const reload = async () => {
    try {
      const r = await adminAxios(token).get("/admin/references");
      setItems(r.data.items || []);
    } catch { setStatus({ state: "error", msg: "Liste alınamadı." }); }
  };
  useEffect(() => { reload(); }, [token]); // eslint-disable-line

  const startNew = () => setEditing({ ...empty });
  const startEdit = (it) => setEditing({ ...empty, ...it, services: it.services || [] });
  const cancel = () => { setEditing(null); setStatus({ state: "idle", msg: "" }); };

  const toggleService = (s) => {
    setEditing(prev => ({
      ...prev,
      services: prev.services.includes(s) ? prev.services.filter(x => x !== s) : [...prev.services, s],
    }));
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/admin/upload-logo`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setEditing(prev => ({ ...prev, logo_url: r.data.url }));
      setStatus({ state: "idle", msg: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setStatus({ state: "error", msg: typeof detail === "string" ? detail : "Yükleme başarısız." });
    } finally { setUploading(false); }
  };

  const save = async () => {
    if (!editing.name.trim()) { setStatus({ state: "error", msg: "Firma adı zorunlu." }); return; }
    setStatus({ state: "loading", msg: "" });
    const payload = {
      name: editing.name.trim(),
      logo_url: editing.logo_url || null,
      services: editing.services,
      display_order: Number(editing.display_order) || 0,
      active: !!editing.active,
    };
    try {
      if (editing.id) {
        await adminAxios(token).put(`/admin/references/${editing.id}`, payload);
      } else {
        await adminAxios(token).post(`/admin/references`, payload);
      }
      setStatus({ state: "success", msg: "Kaydedildi." });
      setEditing(null);
      reload();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setStatus({ state: "error", msg: typeof detail === "string" ? detail : "Hata oluştu." });
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu referans silinsin mi?")) return;
    await adminAxios(token).delete(`/admin/references/${id}`);
    reload();
  };

  const logoSrc = (u) => u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u;

  return (
    <div className="space-y-6" data-testid="admin-references">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight flex items-center gap-3"><BookMarked size={26} /> Referanslar</h1>
          <p className="text-white/50 mt-2">Sitede yayınlanacak müşteri logoları ve hizmet eşleşmeleri.</p>
        </div>
        {!editing && (
          <button onClick={startNew} data-testid="admin-ref-new"
            className="inline-flex items-center gap-2 rounded-full bg-[#FF3B30] text-white px-5 py-2.5 font-semibold hover:bg-white hover:text-[#0A0A0A] transition">
            <Plus size={16} /> Yeni Referans
          </button>
        )}
      </div>

      {status.state === "success" && <Banner ok msg={status.msg} testId="admin-ref-success" />}
      {status.state === "error" && <Banner msg={status.msg} testId="admin-ref-error" />}

      {editing && (
        <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-6 md:p-8 space-y-5" data-testid="admin-ref-form">
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Firma Adı *" testId="ref-name">
              <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="input-dark-admin" />
            </Field>
            <Field label="Sıralama (küçük = önce)" testId="ref-order">
              <input type="number" value={editing.display_order} onChange={(e) => setEditing({ ...editing, display_order: e.target.value })} className="input-dark-admin" />
            </Field>
          </div>

          <Field label="Logo URL" testId="ref-logo-url">
            <input value={editing.logo_url || ""} onChange={(e) => setEditing({ ...editing, logo_url: e.target.value })}
              placeholder="/api/uploads/xxx.png  veya  https://cdn.example.com/logo.png" className="input-dark-admin" />
          </Field>

          <div>
            <span className="block text-xs font-bold tracking-[0.2em] uppercase text-white/50 mb-2">Logo Yükle (PNG/JPG/WEBP/SVG)</span>
            <label className="inline-flex items-center gap-2 cursor-pointer rounded-xl bg-white/[0.05] border border-dashed border-white/20 hover:border-[#FF3B30]/50 px-4 py-3 text-sm" data-testid="ref-logo-upload">
              <Upload size={16} />
              <span>{uploading ? "Yükleniyor..." : "Dosya seç"}</span>
              <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden"
                onChange={(e) => handleUpload(e.target.files?.[0])} disabled={uploading} />
            </label>
            {editing.logo_url && (
              <div className="mt-3 inline-flex items-center gap-3 rounded-xl bg-white/5 border border-white/10 p-3">
                <img src={logoSrc(editing.logo_url)} alt="logo" className="h-10 w-auto object-contain bg-white/5 rounded" />
                <button type="button" onClick={() => setEditing({ ...editing, logo_url: "" })} className="text-white/50 hover:text-[#FF3B30] text-xs">Kaldır</button>
              </div>
            )}
          </div>

          <div>
            <span className="block text-xs font-bold tracking-[0.2em] uppercase text-white/50 mb-3">Hizmetler (birden fazla seçilebilir)</span>
            <div className="flex flex-wrap gap-2" data-testid="ref-services">
              {SERVICE_OPTIONS.map(s => {
                const on = editing.services.includes(s);
                return (
                  <button key={s} type="button" onClick={() => toggleService(s)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${on ? "bg-[#FF3B30] text-white border-[#FF3B30]" : "bg-white/5 text-white/70 border-white/15 hover:border-white/40"}`}>
                    {s}
                  </button>
                );
              })}
            </div>
          </div>

          <label className="inline-flex items-center gap-2 text-sm text-white/70" data-testid="ref-active">
            <input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} />
            Sitede yayınla
          </label>

          <div className="flex gap-3">
            <button onClick={save} data-testid="ref-save" className="inline-flex items-center gap-2 rounded-full bg-[#FF3B30] text-white px-6 py-2.5 font-semibold hover:bg-white hover:text-[#0A0A0A] transition">
              <Save size={16} /> Kaydet
            </button>
            <button onClick={cancel} className="inline-flex items-center gap-2 rounded-full bg-white/10 text-white px-6 py-2.5 font-semibold hover:bg-white/20">
              <X size={16} /> İptal
            </button>
          </div>
        </div>
      )}

      <div className="rounded-2xl bg-white/[0.03] border border-white/10 overflow-hidden" data-testid="admin-ref-list">
        {items.length === 0 ? (
          <div className="p-10 text-center text-white/50 text-sm">Henüz referans eklenmedi.</div>
        ) : (
          <ul className="divide-y divide-white/10">
            {items.map(it => (
              <li key={it.id} className="flex items-center gap-4 p-4 hover:bg-white/[0.02]">
                <div className="flex-none w-14 h-14 bg-white/5 rounded-xl border border-white/10 flex items-center justify-center overflow-hidden">
                  {it.logo_url ? <img src={logoSrc(it.logo_url)} alt={it.name} className="max-w-full max-h-full object-contain" />
                    : <ImageIcon size={20} className="text-white/30" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold flex items-center gap-2">
                    {it.name}
                    {!it.active && <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-white/50">Gizli</span>}
                  </div>
                  <div className="text-xs text-white/50 truncate">{(it.services || []).join(" • ") || "Hizmet seçilmedi"}</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => startEdit(it)} className="p-2 rounded-lg bg-white/5 hover:bg-white/15 text-white/70" title="Düzenle"><Pencil size={15} /></button>
                  <button onClick={() => remove(it.id)} data-testid={`ref-delete-${it.id}`} className="p-2 rounded-lg bg-white/5 hover:bg-[#FF3B30]/20 text-[#FF6B63]" title="Sil"><Trash2 size={15} /></button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <style>{`.input-dark-admin{width:100%;background:rgba(255,255,255,.05);border:1px solid rgba(255,255,255,.12);border-radius:12px;padding:12px 14px;color:#fff;font-size:14px;outline:none;transition:.2s}.input-dark-admin:focus{border-color:#FF3B30;background:rgba(255,255,255,.08)}`}</style>
    </div>
  );
}

function Field({ label, children, testId }) {
  return <label className="block" data-testid={testId}><span className="block text-xs font-bold tracking-[0.2em] uppercase text-white/50 mb-2">{label}</span>{children}</label>;
}

function Banner({ ok = false, msg, testId }) {
  return (
    <div data-testid={testId} className={`flex items-start gap-2 rounded-xl p-3 text-sm ${ok ? "bg-[#34C759]/10 border border-[#34C759]/30 text-[#4ADE80]" : "bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF6B63]"}`}>
      {ok ? <CheckCircle2 size={16} className="mt-0.5" /> : <AlertCircle size={16} className="mt-0.5" />}{msg}
    </div>
  );
}
